import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Package, FileText } from "lucide-react";
import { productAPI, BASE_URL } from "../services/api";

const getImage = (url) => {
  if (!url) return "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=300&q=80";
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
};

const BrandProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productAPI.getBrandProducts(id)
      .then(res => {
        if (res.data?.success) setData(res.data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-[#f5f5f0] pt-36 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-primary-100 rounded mb-8" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-primary-100 aspect-square" />
                <div className="p-3"><div className="h-3 bg-primary-100 rounded w-3/4 mx-auto" /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#f5f5f0] pt-36 flex items-center justify-center">
      <div className="text-center">
        <Building2 className="w-12 h-12 mx-auto mb-3 text-primary-200" />
        <p className="text-primary-500 font-medium">Brand not found</p>
        <button onClick={() => navigate("/")} className="mt-4 text-sm text-secondary-600 hover:underline font-semibold">
          Go to Home
        </button>
      </div>
    </div>
  );

  const { brand, grouped } = data;
  const totalProducts = grouped.reduce((sum, g) => sum + g.products.length, 0);

  const brandLogo = brand.logo_url
    ? (brand.logo_url.startsWith("http") ? brand.logo_url : `${BASE_URL}${brand.logo_url}`)
    : null;

  return (
    <div className="min-h-screen bg-[#f5f5f0] pt-32 pb-16">

      {/* Brand Header */}
      <div className="bg-white border-b border-primary-100 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-primary-400 hover:text-secondary-600 mb-5 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex items-center gap-5">
            {/* Brand Logo */}
            <div className="w-20 h-20 rounded-2xl bg-primary-50 border border-primary-100 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt={brand.name}
                  className="w-full h-full object-contain p-2"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Building2 className="w-8 h-8 text-primary-300" />
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-1 bg-secondary-600 rounded-full" />
                <span className="text-xs font-bold uppercase tracking-widest text-secondary-600">Brand</span>
              </div>
              <h1 className="text-2xl font-extrabold text-primary-800">{brand.name}</h1>
              <p className="text-sm text-primary-400 mt-0.5">
                <span className="font-semibold text-secondary-600">{totalProducts}</span> product{totalProducts !== 1 ? 's' : ''}
                {' '}across <span className="font-semibold text-secondary-600">{grouped.length}</span> categor{grouped.length !== 1 ? 'ies' : 'y'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {grouped.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-primary-100 shadow-sm">
            <Package className="w-12 h-12 mx-auto mb-3 text-primary-200" />
            <p className="text-primary-500 font-medium">No products available for this brand</p>
          </div>
        ) : (
          grouped.map(({ category, products }) => (
            <div key={category.id} className="mb-10">
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-6 bg-secondary-600 rounded-full" />
                <h2 className="text-lg font-extrabold text-primary-800 uppercase tracking-wide">
                  {category.name}
                </h2>
                <span className="bg-secondary-100 text-secondary-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {products.length}
                </span>
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {products.map(product => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}?brand=${encodeURIComponent(brand.name)}&brandId=${brand.id}`}
                    className="group bg-white rounded-2xl border border-primary-100 shadow-sm overflow-hidden hover:shadow-xl hover:border-secondary-300 hover:-translate-y-1 transition-all duration-200"
                  >
                    <div className="relative aspect-square overflow-hidden bg-secondary-50">
                      <img
                        src={getImage(product.image_url)}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={e => { e.target.src = "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=200&q=80"; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-900/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <span className="inline-flex items-center gap-1 bg-secondary-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
                          <FileText className="w-2.5 h-2.5" /> Get Quote
                        </span>
                      </div>
                    </div>
                    <div className="px-3 py-2.5 border-t border-primary-50">
                      {product.type && (
                        <p className="text-[9px] font-semibold text-secondary-500 uppercase tracking-wider mb-0.5 text-center">
                          {product.type.name}
                        </p>
                      )}
                      <p className="text-[11px] text-center font-bold text-primary-700 group-hover:text-secondary-600 transition-colors leading-snug uppercase tracking-wide line-clamp-2">
                        {product.name}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BrandProducts;
